import { PDFDocument } from 'pdf-lib';
import htmlToPdfmake from 'html-to-pdfmake';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { TDocumentDefinitions } from 'pdfmake/interfaces';
import api from '../clients/api';
import services from '.';

const service = {
  convertHtmlToPdfBlob,
  convertHtmlToPdfBlobDocumentEngine,
  convertHtmlToPdfBlobDocumentEnginePublic,
  mergePdfBlobsToBuffer,
};

function cleanMarkTagStyles(html: string): string {
  const div = document.createElement('div');
  div.innerHTML = html;

  const marks = div.querySelectorAll('mark');
  marks.forEach((mark) => {
    const style = mark.getAttribute('style');
    if (style && style.includes('color: inherit')) {
      mark.setAttribute('style', style.replace('color: inherit', ''));
    }
  });

  return div.innerHTML;
}

async function replaceImagesWithBase64(
  html: string,
  imageFetcher: (url: string) => Promise<string>
): Promise<string> {
  // Max width for images in PDF (A4 page), accounting for margins and padding, in pixels.
  const MAX_IMAGE_WIDTH_IN_PDF = 681.44;

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const images = Array.from(doc.querySelectorAll('img'));

  const imagePromises = images.map(async (img) => {
    const srcUrl = img.getAttribute('src');
    if (srcUrl) {
      try {
        const imageBase64 = await imageFetcher(srcUrl);
        img.setAttribute('src', imageBase64);
        img.style.maxWidth = `${MAX_IMAGE_WIDTH_IN_PDF}px`;
        img.style.maxHeight = 'auto';
      } catch (error) {
        throw new Error(`Error fetching image: ${srcUrl}`);
      }
    }
  });

  await Promise.all(imagePromises);

  return doc.body.innerHTML;
}

/**
 * Converts HTML to PDF blob
 * @param input - Either an HTML string or an object with elementId to extract HTML from DOM
 * @param options - Optional configuration including renderMethod and custom image fetcher
 *   - 'pdfmake': Fast, smaller files, text is selectable, but emojis don't render well (default)
 *   - 'document-engine': Professional quality, perfect emoji rendering, fully selectable text, server-side processing
 * @param accessToken - Public access token for the document
 * @returns Promise<Blob> - PDF blob
 */
async function convertHtmlToPdfBlob(
  input: string | { elementId: string },
  options?: {
    imageFetcher?: (url: string) => Promise<{ imageBase64: string }>;
    renderMethod?: 'pdfmake' | 'document-engine';
  },
  accessToken?: string
): Promise<Blob> {
  let html: string;

  // Extract HTML based on input type
  if (typeof input === 'string') {
    html = input;
  } else {
    const container = document.getElementById(input.elementId);
    if (!container) throw new Error(`Element with ID '${input.elementId}' not found.`);
    html = container.innerHTML;
  }

  // Route to appropriate rendering method
  const renderMethod = options?.renderMethod || 'pdfmake';

  if (renderMethod === 'document-engine') {
    if (accessToken) {
      return convertHtmlToPdfBlobDocumentEnginePublic(html, accessToken);
    }
    return convertHtmlToPdfBlobDocumentEngine(html);
  }

  // Default pdfmake implementation
  // Remove inherited color styles from mark tags to avoid color issues in PDF
  if (html.includes('<mark')) html = cleanMarkTagStyles(html);

  // Replace images with base64 if present
  if (html.includes('<img')) {
    const defaultImageFetcher = async (url: string) => {
      const result = await services.image.getImageBase64(url);
      return result.imageBase64;
    };
    const imageFetcher = options?.imageFetcher
      ? async (url: string) => {
        const result = await options.imageFetcher!(url);
        return result.imageBase64;
      }
      : defaultImageFetcher;

    html = await replaceImagesWithBase64(html, imageFetcher);
  }

  const pdfmakeContent = htmlToPdfmake(html);

  const documentDefinition: TDocumentDefinitions = {
    content: pdfmakeContent,
  };

  return new Promise((resolve, reject) => {
    pdfMake.vfs = pdfFonts;
    pdfMake.createPdf(documentDefinition).getBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error('Error generating PDF'));
      }
    });
  });
}

/**
 * Converts HTML to PDF using PSPDFKit Document Engine
 * This method provides professional-grade PDF generation with proper emoji and font support
 * @param html - HTML string to convert
 * @returns Promise<Blob> - PDF blob
 */
async function convertHtmlToPdfBlobDocumentEngine(html: string): Promise<Blob> {
  return api.document.convertHtmlToPdf(html);
}

/**
 * Converts HTML to PDF using PSPDFKit Document Engine
 * This method provides professional-grade PDF generation with proper emoji and font support
 * @param html - HTML string to convert
 * @param accessToken - Public access token for the document
 * @returns Promise<Blob> - PDF blob
 */
async function convertHtmlToPdfBlobDocumentEnginePublic(html: string, accessToken: string): Promise<Blob> {
  return api.document.convertHtmlToPdfPublic(html, accessToken);
}

/**
 * Merges multiple PDF blobs into a single PDF buffer
 * @param partBlobs - Array of PDF blobs to merge
 * @param title - Title for the merged PDF document
 * @returns Promise<Uint8Array> - Merged PDF as buffer
 */
async function mergePdfBlobsToBuffer(partBlobs: Blob[], title: string): Promise<Uint8Array> {
  const mergedPdf = await PDFDocument.create();

  for (const blob of partBlobs) {
    const arrayBuffer = await blob.arrayBuffer();
    const pdf = await PDFDocument.load(arrayBuffer);
    const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
    copiedPages.forEach((page) => mergedPdf.addPage(page));
  }

  mergedPdf.setTitle(title);
  mergedPdf.setAuthor('');

  const pdfBytes = await mergedPdf.save();

  return pdfBytes;
}

export default service;

